import clsx from "clsx";
import { chartNumber, linearScale, type ChartNumber } from "./chartMath";
import styles from "./charts.module.css";

export interface SparklineProps {
  values: readonly ChartNumber[];
  /** Summary for screen readers ("Price fell from CA$14 to CA$12 over 6 months"); omit to hide it. */
  ariaLabel?: string;
  width?: number;
  height?: number;
  series?: 1 | 2 | 3 | 4 | 5 | 6;
  className?: string;
}

/** A tiny trend line without axes, for table cells and stats. Missing values leave gaps. */
export function Sparkline({ values, ariaLabel, width = 96, height = 24, series = 1, className }: SparklineProps) {
  const numbers = values.map(chartNumber);
  const defined = numbers.filter((value): value is number => value !== null);
  const min = defined.length ? Math.min(...defined) : 0;
  const max = defined.length ? Math.max(...defined) : 1;
  const scaleX = linearScale([0, Math.max(numbers.length - 1, 1)], [2, width - 2]);
  const scaleY = linearScale([min, max === min ? min + 1 : max], [height - 2, 2]);
  let path = "";
  let penDown = false;
  numbers.forEach((value, index) => {
    if (value === null) {
      penDown = false;
      return;
    }
    path += `${penDown ? "L" : "M"}${scaleX(index).toFixed(1)},${scaleY(value).toFixed(1)}`;
    penDown = true;
  });
  const lastIndex = numbers.findLastIndex((value) => value !== null);
  const last = lastIndex >= 0 ? numbers[lastIndex] : null;
  return (
    <svg
      className={clsx(styles.sparkline, className)}
      data-series={series}
      viewBox={`0 0 ${width} ${height}`}
      width={width}
      height={height}
      role={ariaLabel ? "img" : undefined}
      aria-label={ariaLabel}
      aria-hidden={ariaLabel ? undefined : true}
    >
      <path className={styles.line} d={path} />
      {last !== null && last !== undefined ? (
        <circle className={styles.point} cx={scaleX(lastIndex)} cy={scaleY(last)} r={2} />
      ) : null}
    </svg>
  );
}
